import { css } from '@compiled/react';
import { useIntl, FormattedMessage } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { N0 } from '@atlaskit/theme/colors';

import { Link } from '@/libraries/design/components/link/src/link';

import { Picture } from '../../image/src/image';

const containerCss = css({
	alignItems: 'flex-start',
	justifyContent: 'center',
	// eslint-disable-next-line local/ensure-wac-token-usage
	backgroundColor: '#000000',
	display: 'flex',
	color: 'white',
	overflow: 'visible',
	padding: '64px 107px 64px 171px',
	[media.below.md]: {
		gap: '22px',
		maxWidth: '640px',
		padding: '64px 54px 108px',
	},
	[media.below.sm]: {
		flexDirection: 'column-reverse',
		clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 90%)',
	},
	clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 85%)',
	gap: 72,
});

const sectionLeftCss = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	textAlign: 'left',
	gap: '16px',
	paddingBlockEnd: '10px',
	[media.below.sm]: {
		paddingBlockEnd: '25px',
	},
});

const sectionLeftTextCss = css({
	margin: '0px',
	color: N0,
});

const headingCss = css({
	fontSize: '32px',
	lineHeight: '38px',
	[media.below.md]: {
		fontSize: '28px',
		lineHeight: '34px',
	},
	[media.below.sm]: {
		fontSize: '24px',
		lineHeight: '30px',
	},
});

const svgStyles = css({
	width: '170px',
	height: '181px',
	[media.below.md]: {
		width: '86px',
		height: '91.61px',
	},
});

export interface QuestionsCtaProps {
	questionsHeader?: string | undefined;
	priceAndLicensingLinkText?: string | undefined;
	ctaHref: string | undefined;
	imageHref?: string | undefined;
	questionsBody?: string | undefined;
	product?: string | undefined;
}

// todo as part of PRICING-1107 epic: integrate strings from Contentful
export const QuestionsCta = ({
	questionsHeader,
	questionsBody,
	priceAndLicensingLinkText,
	ctaHref,
	imageHref = 'https://dam-cdn.atl.orangelogic.com/AssetLink/0s31nt386q2lt662011182pvct037sp1.svg',
	product,
}: QuestionsCtaProps) => {
	const intl = useIntl();

	if (!ctaHref || (!product && !questionsBody)) {
		return null;
	}

	const fallbackQuestionsHeader = intl.formatMessage({
		id: 'PricingPage.QuestionsCta.questionsHeader',
		defaultMessage: 'Questions?',
	});

	const fallbackQuestionsBody = (
		<FormattedMessage
			id="PricingPage.QuestionsCta.questionsBody"
			defaultMessage="We got answers. Read our full list of common questions and answers for general cloud pricing and licensing or specific {product} pricing and licensing."
			values={{ product }}
		/>
	);

	const text = intl.formatMessage({
		id: 'PricingPage.QuestionsCta.priceAndLicensingLinkText',
		defaultMessage: 'See price and licensing page',
	});

	return (
		<div css={containerCss}>
			<div css={sectionLeftCss}>
				<h3 css={[headingCss, sectionLeftTextCss]} data-testid="questions-cta-header">
					{questionsHeader ?? fallbackQuestionsHeader}
				</h3>
				<p css={sectionLeftTextCss} data-testid="questions-cta-body">
					{questionsBody ?? fallbackQuestionsBody}
				</p>
				<Link
					text={priceAndLicensingLinkText ?? text}
					aria-label={priceAndLicensingLinkText ?? text}
					href={ctaHref}
					anchorProps={{
						'data-testid': 'questions-cta-link',
					}}
					bgColor={'bgBlackBlue'}
				/>
			</div>
			<Picture
				image={{
					src: imageHref,
					alt: '',
				}}
				css={svgStyles}
			/>
		</div>
	);
};
QuestionsCta.displayName = 'QuestionsCTASection';
